import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import classNames from 'classnames';
import { TemplateClassNames } from '../../../../../../types/template-layouts';
import { Button } from '../../../../../general-ui/button/button';
import React from 'react';
import Icon, { ICONS, COLORS } from '../../../../../general-ui/icon';
import { TypographyItem } from '../../../../../general-ui/typography-item/typography-item';
import Slider from 'react-slick';
import { LanguagesAbbr, Product as ProductType } from '../../../../../../types/working-model';

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function PrevArrow(props: any): JSX.Element {
	const { className, style, onClick } = props;

	return (
		<div onClick={onClick} className={className} style={{ ...style }}>
			<Icon name={ICONS.KEYBOARD_ARROW_LEFT} size={20} color={COLORS.WHITE} />
		</div>
	);
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function NextArrow(props: any) {
	const { className, style, onClick } = props;

	return (
		<div onClick={onClick} className={className} style={{ ...style }}>
			<Icon name={ICONS.KEYBOARD_ARROW_RIGHT} size={20} color={COLORS.WHITE} />
		</div>
	);
}

export const settings = {
	dots: false,
	infinite: true,
	speed: 500,
	slidesToShow: 1,
	slidesToScroll: 1,
};

interface IProductProps {
	id: string;
	product: ProductType;
	editProduct: () => void;
	replaceProduct: () => void;
	removeProduct: () => void;
	template: TemplateClassNames;
	language: LanguagesAbbr
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export default function Product(props: IProductProps): JSX.Element {
	const { attributes, listeners, setNodeRef, transform } = useSortable({
		id: props.id,
	});

	const {
		product,
		editProduct,
		replaceProduct,
		removeProduct,
		template,
		language
	} = props;

	const style = {
		transform: CSS.Transform.toString(transform),
		transition: 'none',
	};

	function getValue(key: keyof ProductType) {
		return product?.[key]?.[language] ?? product?.[key]?.base;
	}
	return (
		<li
			ref={setNodeRef}
			style={style}
			{...attributes}
			{...listeners}
			className="product"
		>
			<div className="product-item-image-container">
				<Slider
					prevArrow={<PrevArrow />}
					nextArrow={<NextArrow />}
					{...settings}
				>
					{product.images.map((image: string, index: number) => (
						<React.Fragment key={index}>
							<img src={image} alt={image} aria-hidden="true" />
						</React.Fragment>
					))}
				</Slider>
				{
					product?.sale_price?.base && (
						<span className="product-item-label">Sale</span>
					)
				}
			</div>
			<div className="product-item-content">
				<TypographyItem
					className={classNames('product-item-title stable', {
						'evt-heading-3': template === TemplateClassNames.Apollo,
						'evt-heading-4': template !== TemplateClassNames.Apollo,
					})}
					tagName="h3"
				>
					{getValue('product_title')}
				</TypographyItem>
				<div className="price-container">
					<TypographyItem
						className={classNames('product-item-price stable', {
							'evt-heading-5': template === TemplateClassNames.Apollo || template === TemplateClassNames.O2,
							'evt-caption-1': template === TemplateClassNames['Studio 54'],
						})}
					>
						{
							product?.price?.base ? (
								`$ ${getValue('sale_price') || getValue('price')}`
							) : null
						}
					</TypographyItem>
					{
						(product?.sale_price?.base && product?.price?.base) ? (
							<TypographyItem className={classNames('product-item-old-price evt-body-text-2 stable')}>
								$ {getValue('price')}
							</TypographyItem>
						) : null
					}
				</div>
				<TypographyItem
					className={classNames(
						'evt-body-text-2 product-item-description',
						{ 'increase-description': !product?.price?.base }
					)}
					tagName="p">
					{getValue('description')}
				</TypographyItem>
				<Button classButton={classNames('primary')} template={template}>
					{getValue('button_text') || 'Learn More'}
				</Button>
			</div>


			<div className="display-controls" >
				<button className="round product-control" onClick={editProduct}>
					<Icon name={ICONS.EDIT} size={12} color={COLORS.DARK_GREY} />
				</button>
				<button className="round product-control" onClick={replaceProduct}>
					<Icon name={ICONS.ARROW_CIRCLE} size={12} color={COLORS.DARK_GREY} />
				</button>
				<button className="round product-control" onClick={removeProduct}>
					<Icon name={ICONS.DELETE} size={12} color={COLORS.DARK_GREY} />
				</button>
			</div>
		</li>
	);
}
